import { useCallback, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useNotifications } from "hooks/notifications"
import { useAccountAlias } from "resources/account"
import { navigateTo } from "helpers/navigation"
import { track } from "helpers/analytics"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"

interface SkillDownloadResponse {
  url: string
}

function isDownloadResponse(value: unknown): value is SkillDownloadResponse {
  return typeof value === "object"
    && value !== null
    && typeof (value as { url?: unknown }).url === "string"
}

function isSameOriginDownloadUrl(url: string): boolean {
  if (url.startsWith("/")) {
    return true
  }

  try {
    return new URL(url, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

interface UseSkillDownloadResult {
  downloadSkill: () => void
  isDownloading: boolean
}

/**
 * Imperative, authenticated skill download.
 *
 * The server endpoint returns `{ url }` rather than a redirect or blob. Cross-origin signed
 * CloudFront URLs are opened via navigation — auth is header-only and a 302 would drop it.
 * Same-origin dev `/raw` routes are fetched with `X-OmegaPoint-Code` and saved as a blob,
 * because browser navigation cannot attach that header.
 *
 * `id` contains a slash (`<plugin>/<slug>`), so it must be `encodeURIComponent`-d into the path.
 */
export function useSkillDownload(skill: SkillCatalogSkill): UseSkillDownloadResult {
  const { id, tier, name, version } = skill
  const dispatch = useDispatch()
  const account = useAccountAlias()
  const notifications = useNotifications()
  // useRef latch is the actual in-flight guard: it blocks a second click synchronously (two
  // clicks in one frame both pass a useState check before the re-render flips it). isDownloading
  // is UI-only (button spinner/disabled) and stays out of the callback deps so it's stable.
  const inFlightRef = useRef(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const downloadSkill = useCallback(() => {
    if (inFlightRef.current) {
      return
    }
    inFlightRef.current = true
    setIsDownloading(true)

    const url = `/api/${account}/skills/download/${tier}/${encodeURIComponent(id)}`

    new Promise((resolve, reject) => {
      dispatch(Creators.get(url, resolve, reject))
    })
      .then((response) => {
        if (!isDownloadResponse(response)) {
          throw new Error("Skill download response did not contain a url")
        }

        try {
          track(SKILL_EVENTS.SKILL_DOWNLOADED, { skillId: id, skillName: name, version, tier })
        } catch {
          // analytics error must not interrupt the download
        }

        if (isSameOriginDownloadUrl(response.url)) {
          return new Promise((resolve, reject) => {
            dispatch(Creators.download(response.url, resolve, reject))
          })
        }

        navigateTo(response.url)
      })
      .catch(() => {
        notifications.error("Unable to download workflow. Please try again.")
      })
      .finally(() => {
        inFlightRef.current = false
        setIsDownloading(false)
      })
  }, [account, dispatch, id, name, notifications, tier, version])

  return { downloadSkill, isDownloading }
}
